import { createContext, useContext, useEffect, useState } from "react";
import { api, ADMIN_TOKEN_KEY } from "@/lib/api";
import { adminAuth, authMessage, staffLoginEmail } from "@/lib/supabase";

/*
  Website admin sign-in: username + password, checked by Supabase Auth (username -> internal login address). Access is decided by the server: /auth/me
  only succeeds for staff with the admin role, checked on every request.
*/
const AuthContext = createContext(null);

const mirror = (session) => {
  if (session?.access_token) localStorage.setItem(ADMIN_TOKEN_KEY, session.access_token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
  return session?.access_token || "";
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);       // null = checking, false = not authed, object = authed

  const load = async (session) => {
    const token = mirror(session);
    if (!token) { setUser(false); return false; }
    try {
      const { data } = await api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } });
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  };

  useEffect(() => {
    adminAuth.auth.getSession().then(({ data }) => load(data.session));
    const { data } = adminAuth.auth.onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED") { mirror(session); return; }
      setTimeout(() => load(session), 0);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const login = async (username, password, turnstileToken) => {
    const { data, error } = await adminAuth.auth.signInWithPassword({
      email: staffLoginEmail(username), password, options: { captchaToken: turnstileToken || undefined } });
    if (error) throw new Error(/invalid|banned/i.test(error.message) ? "Invalid username or password" : authMessage(error));
    const me = await load(data.session);
    if (!me) {
      await adminAuth.auth.signOut();
      mirror(null);
      throw new Error("This account doesn't have website admin access.");
    }
    return { user: me };
  };

  const logout = async () => {
    await adminAuth.auth.signOut();
    mirror(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
